import React, { useState } from 'react';
import { Globe, Plus, X, Edit2, Save, AlertCircle, Info, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { useAIContext } from '../hooks/useAIContext';
import { useAuth } from '../hooks/useAuth';

const CATEGORIAS_GLOBAL = [
  { value: 'geral',     label: '🌐 Geral' },
  { value: 'negocio',   label: '💼 Negócio' },
  { value: 'tom',       label: '💬 Tom' },
  { value: 'processo',  label: '⚙️ Processo' },
  { value: 'restricao', label: '🚫 Restrição' },
];

const getCategoriaStyle = (cat) => {
  const map = {
    geral:     'bg-slate-50 border-slate-200',
    negocio:   'bg-blue-50 border-blue-200',
    tom:       'bg-pink-50 border-pink-200',
    processo:  'bg-indigo-50 border-indigo-200',
    restricao: 'bg-red-50 border-red-200',
  };
  return map[cat] || map.geral;
};

/**
 * Modal de edição do Contexto Global da Aston Capital.
 * Aberto pelo botão "Contexto IA" no header do Dashboard.
 *
 * Cada item vira parte do system_prompt de TODO agente IA.
 * Mudança aqui tem impacto sistêmico — por isso cada item tem ativo/inativo
 * em vez de deletar direto.
 */
const AIContextGlobalModal = ({ onClose }) => {
  const { user } = useAuth();
  const {
    globalContext,
    loadingGlobal,
    createGlobal,
    updateGlobal,
    deleteGlobal,
    refetchGlobal,
  } = useAIContext();

  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  // Form de novo
  const [criando, setCriando] = useState(false);
  const [novoTitulo, setNovoTitulo] = useState('');
  const [novaChave, setNovaChave] = useState('');
  const [novaCategoria, setNovaCategoria] = useState('geral');
  const [novoConteudo, setNovoConteudo] = useState('');
  const [salvandoNovo, setSalvandoNovo] = useState(false);

  const iniciarEdicao = (item) => {
    setEditingId(item.id);
    setEditData({
      titulo: item.titulo,
      categoria: item.categoria,
      conteudo: item.conteudo,
      ativo: item.ativo,
      ordem: item.ordem,
    });
  };

  const cancelarEdicao = () => {
    setEditingId(null);
    setEditData({});
  };

  const salvarEdicao = async (id) => {
    if (!editData.titulo?.trim() || !editData.conteudo?.trim()) {
      setError('Título e conteúdo são obrigatórios');
      return;
    }
    setSavingId(id);
    setError(null);
    try {
      await updateGlobal(id, editData, user?.email);
      cancelarEdicao();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const toggleAtivo = async (item) => {
    setSavingId(item.id);
    try {
      await updateGlobal(item.id, { ativo: !item.ativo }, user?.email);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleCriar = async () => {
    if (!novoTitulo.trim() || !novaChave.trim() || !novoConteudo.trim()) {
      setError('Chave, título e conteúdo são obrigatórios');
      return;
    }
    setSalvandoNovo(true);
    setError(null);
    try {
      await createGlobal({
        chave: novaChave,
        titulo: novoTitulo,
        conteudo: novoConteudo,
        categoria: novaCategoria,
        ordem: globalContext.length + 1,
      }, user?.email);
      setCriando(false);
      setNovoTitulo('');
      setNovaChave('');
      setNovoConteudo('');
      setNovaCategoria('geral');
    } catch (err) {
      setError(err.message.includes('duplicate') ? 'Chave já existe — escolha outra' : err.message);
    } finally {
      setSalvandoNovo(false);
    }
  };

  const handleRemover = async (item) => {
    if (!window.confirm(`Remover "${item.titulo}" permanentemente?\n\nAlternativa: usar o botão "desativar" em vez de remover — preserva o texto caso queira reativar depois.`)) return;
    setSavingId(item.id);
    try {
      await deleteGlobal(item.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">

        <div className="p-5 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <Globe className="text-indigo-600" size={22} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Contexto IA Global</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Diretrizes que alimentam TODO prompt IA do sistema
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={22} />
            </button>
          </div>

          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-800 flex items-start gap-1.5">
            <Info size={12} className="flex-shrink-0 mt-0.5" />
            <div>
              <strong>Atenção — mudança sistêmica:</strong> cada item ativo aqui vai para o prompt de TODOS os agentes IA
              (briefings, detecções, sugestões). Edições impactam todas as gerações futuras. Use <strong>Desativar</strong> em vez
              de remover quando possível.
            </div>
          </div>
        </div>

        {/* Botão adicionar */}
        {!criando && (
          <div className="px-4 pt-3 flex-shrink-0">
            <button
              onClick={() => setCriando(true)}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 flex items-center gap-1"
            >
              <Plus size={12} /> Novo item de contexto global
            </button>
          </div>
        )}

        {/* Form novo */}
        {criando && (
          <div className="mx-4 mt-3 p-3 border-2 border-indigo-300 bg-indigo-50/50 rounded flex-shrink-0">
            <div className="text-xs font-semibold text-indigo-800 mb-2">Novo item</div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <input
                type="text"
                value={novaChave}
                onChange={(e) => setNovaChave(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                className="col-span-1 px-2 py-1 border border-slate-300 rounded text-xs"
                placeholder="chave_unica (snake_case)"
                disabled={salvandoNovo}
              />
              <input
                type="text"
                value={novoTitulo}
                onChange={(e) => setNovoTitulo(e.target.value)}
                className="col-span-1 px-2 py-1 border border-slate-300 rounded text-xs"
                placeholder="Título do item"
                disabled={salvandoNovo}
              />
              <select
                value={novaCategoria}
                onChange={(e) => setNovaCategoria(e.target.value)}
                className="col-span-1 px-2 py-1 border border-slate-300 rounded text-xs"
                disabled={salvandoNovo}
              >
                {CATEGORIAS_GLOBAL.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <textarea
              value={novoConteudo}
              onChange={(e) => setNovoConteudo(e.target.value)}
              rows={4}
              placeholder="Conteúdo a incluir no prompt. Seja específico e acionável."
              className="w-full px-2 py-1 border border-slate-300 rounded text-xs resize-none mb-2"
              disabled={salvandoNovo}
            />
            <div className="flex justify-end gap-1">
              <button
                onClick={() => { setCriando(false); setError(null); }}
                className="px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={handleCriar}
                disabled={salvandoNovo}
                className="px-2 py-0.5 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-0.5"
              >
                <Save size={10} /> {salvandoNovo ? 'Salvando…' : 'Criar'}
              </button>
            </div>
          </div>
        )}

        {/* Lista */}
        <div className="flex-grow overflow-y-auto p-4">
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1.5">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {loadingGlobal && (
            <div className="text-center py-8 text-sm text-slate-500">Carregando…</div>
          )}

          {!loadingGlobal && globalContext.length === 0 && (
            <div className="text-center py-10 text-sm text-slate-500">
              Nenhum item de contexto global ainda.
            </div>
          )}

          <div className="space-y-2">
            {globalContext.map(item => {
              const isEditing = editingId === item.id;
              const isExpanded = expandedId === item.id;
              const estilo = getCategoriaStyle(item.categoria);

              return (
                <div key={item.id} className={`${estilo} border rounded p-3 ${!item.ativo ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-2">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      className="flex-shrink-0 mt-0.5 text-slate-500"
                    >
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>

                    <div className="flex-grow min-w-0">
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={editData.titulo}
                              onChange={(e) => setEditData({...editData, titulo: e.target.value})}
                              className="px-2 py-1 border border-slate-300 rounded text-xs font-medium"
                              placeholder="Título"
                            />
                            <select
                              value={editData.categoria}
                              onChange={(e) => setEditData({...editData, categoria: e.target.value})}
                              className="px-2 py-1 border border-slate-300 rounded text-xs"
                            >
                              {CATEGORIAS_GLOBAL.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          </div>
                          <textarea
                            value={editData.conteudo}
                            onChange={(e) => setEditData({...editData, conteudo: e.target.value})}
                            rows={6}
                            className="w-full px-2 py-1 border border-slate-300 rounded text-xs resize-none bg-white"
                          />
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={cancelarEdicao}
                              className="px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200 rounded"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={() => salvarEdicao(item.id)}
                              disabled={savingId === item.id}
                              className="px-2 py-0.5 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-0.5"
                            >
                              <Save size={10} />{savingId === item.id ? 'Salvando…' : 'Salvar'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[10px] font-mono text-slate-500">{item.chave}</span>
                              <span className="text-sm font-semibold text-slate-800 truncate">{item.titulo}</span>
                              {!item.ativo && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">inativo</span>
                              )}
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <button
                                onClick={() => toggleAtivo(item)}
                                disabled={savingId === item.id}
                                className="p-1 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                title={item.ativo ? 'Desativar (preserva)' : 'Reativar'}
                              >
                                {item.ativo ? <Eye size={12} /> : <EyeOff size={12} />}
                              </button>
                              <button
                                onClick={() => iniciarEdicao(item)}
                                className="p-1 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                title="Editar"
                              >
                                <Edit2 size={12} />
                              </button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap bg-white/50 rounded p-2 border border-black/5">
                              {item.conteudo}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-between text-[10px] text-slate-500">
          <span>{globalContext.filter(i => i.ativo).length} ativos / {globalContext.length} total</span>
          <span>Mudanças afetam prompts IA imediatamente</span>
        </div>
      </div>
    </div>
  );
};

export default AIContextGlobalModal;
